import { Injectable } from "@angular/core";
import { DISABLE_APPSFLYER_COOKIE_KEY } from "app/common";
import { environment } from "environments/environment";
import { CookieService } from "ngx-cookie-service";
import { AppsFlyerConsentService } from "./appsflyer-consent.service";
import { distinctUntilChanged } from "rxjs";
import { DeviceRecognizerHelper } from "app/core/helpers/device-recognizer.helper";

type UserAgentType = {
  devicePlatform: "IOS" | "ANDROID" | "WEB";
  executionContext: "WEBVIEW" | "BROWSER";
  browserName: "Safari" | "Chrome" | "Firefox" | "Opera" | "SamsungInternet" | "Edge" | "WebView" | "Unknown";
  userAgent: string;
};

@Injectable({
  providedIn: "root"
})
export class AppsFlyerService {
  private appsFlyerInitialized = false;

  constructor(
    private cookieService: CookieService,
    private consent: AppsFlyerConsentService
  ) {
    this.consent.analyticsConsentChanges$.pipe(distinctUntilChanged()).subscribe((allow) => {
      if (this.cookieService.get(DISABLE_APPSFLYER_COOKIE_KEY) === "false") {
        this.initAppsFlyer();
      }
    });
  }

  private initAppsFlyer(): void {
    if (this.appsFlyerInitialized) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;

    (function (t: any, e: any, n: any, s: any, a: any, c: any, i: any, o?: any, p?: any) {
      t.AppsFlyerSdkObject = a;

      t.AF =
        t.AF ||
        function () {
          (t.AF.q = t.AF.q || []).push([Date.now()].concat(Array.prototype.slice.call(arguments)));
        };

      t.AF.id = t.AF.id || i;
      t.AF.plugins = t.AF.plugins || {};

      o = e.createElement(n);
      p = e.getElementsByTagName(n)[0];

      o.async = 1;
      o.src =
        "https://websdk.appsflyer.com?" +
        (c && c.length > 0 ? "st=" + c.split(",").sort().join(",") + "&" : "") +
        ((i as any)?.length > 0 ? "af_id=" + i : "");

      p.parentNode.insertBefore(o, p);
    })(window, document, "script", 0, "AF", "pba", { pba: { webAppId: environment.appsFlyerWebAppId } });

    this.appsFlyerInitialized = true;
  }

  public static logEvent(eventName: string, pageUrl?: string): void {
    const af = (window as any)?.AF;
    if (typeof af !== "function") return;

    const ctx = AppsFlyerService.getTrackingContext();

    af("pba", "event", {
      eventType: "EVENT",
      eventName,
      eventValue: {
        pageUrl: pageUrl,
        devicePlatform: ctx.devicePlatform,
        executionContext: ctx.executionContext,
        browserName: ctx.browserName,
        userAgent: ctx.userAgent
      }
    });
  }

  private static getTrackingContext(): UserAgentType {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";

    const devicePlatform = DeviceRecognizerHelper.isIos()
      ? "IOS"
      : DeviceRecognizerHelper.isAndroid()
        ? "ANDROID"
        : "WEB";

    const isWebView = DeviceRecognizerHelper.isWebView();
    const executionContext = isWebView ? "WEBVIEW" : "BROWSER";

    if (isWebView) {
      return { devicePlatform, executionContext, browserName: "WebView", userAgent: ua };
    }

    if (/Edg\/\d+/i.test(ua) || /EdgiOS\/\d+/i.test(ua)) {
      return { devicePlatform, executionContext, browserName: "Edge", userAgent: ua };
    }
    if (/OPR\/\d+/i.test(ua) || /OPiOS\/\d+/i.test(ua)) {
      return { devicePlatform, executionContext, browserName: "Opera", userAgent: ua };
    }
    if (/Firefox\/\d+/i.test(ua) || /FxiOS\/\d+/i.test(ua)) {
      return { devicePlatform, executionContext, browserName: "Firefox", userAgent: ua };
    }
    if (/SamsungBrowser\/\d+/i.test(ua)) {
      return { devicePlatform, executionContext, browserName: "SamsungInternet", userAgent: ua };
    }
    if (/Chrome\/\d+/i.test(ua) || /CriOS\/\d+/i.test(ua)) {
      return { devicePlatform, executionContext, browserName: "Chrome", userAgent: ua };
    }
    if (DeviceRecognizerHelper.isIosSafari()) {
      return { devicePlatform, executionContext, browserName: "Safari", userAgent: ua };
    }

    return { devicePlatform, executionContext, browserName: "Unknown", userAgent: ua };
  }
}
